/**
 * Represents a video player with controls. Video begins playing when the user scrolls to the video and pauses when the video is outside of the viewport.
 * @param {HTMLElement} container - The container element for the video player.
 * @property {HTMLElement} controls.container - The container element for the video controls.
 * @property {HTMLElement} controls.icons.play - The play icon element.
 * @property {HTMLElement} controls.icons.pause - The pause icon element.
 * @property {HTMLElement} video - The video element.
 * @property {boolean} isPlaying - A boolean that indicates whether the video is playing.
 * @property {boolean} playedOnce - A boolean that indicates whether the video has played once.
 */

export class VideoPlayer {
  constructor(container) {
    this.container = container;
    this.controls = {
      container: container.querySelector("video-controls"),
      icons: {
        play: container.querySelector(".play-icon"),
        pause: container.querySelector(".pause-icon"),
      },
    };
    this.video = container.querySelector("video");
    this.isPlaying = false;
    this.playedOnce = false;

    this._init();
  }

  _triggerControl = (e) => {
    this.controls.icons.play.style.display = this.isPlaying ? "block" : "none";
    this.controls.icons.pause.style.display = this.isPlaying ? "none" : "block";

    const ariaLabel = this.isPlaying ? "play video" : "pause video";
    this.controls.container.setAttribute("aria-label", ariaLabel);

    if (this.isPlaying) {
      this.video.pause();
    } else {
      this.video.play();
    }

    this.isPlaying = !this.isPlaying;
  };

  _setObserver = () => {
    const ob = new IntersectionObserver(this._obCallback);
    ob.observe(this.controls.container);
  };

  _obCallback = (payload) => {
    if (payload[0].isIntersecting && !this.isPlaying) {
      // if video has already played once do not autoplay it again.
      if (!this.playedOnce) {
        this._triggerControl();
      }
    }
  };

  _handleVideoEnd = () => {
    this.playedOnce = true;
    this._triggerControl();
  };

  _init = () => {
    if (!this.video) {
      console.warn("VideoPlayer: No video element found.");
      return;
    }

    this.video.addEventListener("ended", this._handleVideoEnd, false);

    if (this.controls.container) {
      if (!this.controls.icons.play || !this.controls.icons.pause) {
        console.warn("VideoPlayer: Video controls elements missing.");
        return;
      }

      this.controls.container.addEventListener(
        "click",
        this._triggerControl,
        false
      );
      this.controls.container.addEventListener("keydown", (e) => {
        if (e.key == "Enter") {
          this._triggerControl();
        }
      });
    }

    if (this.container.dataset.autoplay) {
      this._setObserver();
    }
  };
}
